// OfferLetterForm — HR fills CTC / position / terms before generating.
// Pre-fills from /offer-form-data (CV + job + company defaults), all editable.

import React, { useEffect, useState } from "react";
import { X, Loader2, FileSignature, Plus, Trash2 } from "lucide-react";
import { offerLetterApi } from "../../../services/offerLetter.service";
import { toast } from "react-toastify";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const FREQUENCIES = ["year", "month"];
const LEVELS = ["Intern", "Junior", "Mid", "Senior", "Lead", "Principal", "Manager", "Director"];
const WORK_TYPES = ["onsite", "hybrid", "remote"];

export default function OfferLetterForm({ isOpen, onClose, resumeId, candidateName, onGenerated }) {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [meta, setMeta]         = useState({ candidate: candidateName, company: null });
  const [form, setForm]         = useState(null);

  useEffect(() => {
    if (!isOpen || !resumeId) return;
    setLoading(true);
    (async () => {
      try {
        const r = await offerLetterApi.getOfferFormData(resumeId);
        const d = r.data || r; // service may unwrap
        const def = d.formDefaults || {};
        setMeta({
          candidate: d.candidateInfo?.name || candidateName,
          company: d.company || null,
          skills: d.candidateInfo?.skills || [],
        });
        setForm({
          positionTitle: def.position?.title || d.jobInfo?.title || "",
          positionLevel: def.position?.level || "Senior",
          department:    def.position?.department || d.jobInfo?.department || "",
          ctcAmount:     "",   // always start blank — HR must enter their own CTC
          ctcCurrency:   def.salary?.currency || "INR",
          ctcFrequency:  (def.salary?.frequency === "annual" ? "year" : def.salary?.frequency) || "year",
          startDate:     def.startDate || new Date(Date.now() + 30 * 864e5).toISOString().split("T")[0],
          benefits:      def.benefits?.length ? def.benefits : ["Health Insurance", "Paid Time Off"],
          reportingManager: def.reportingManager && def.reportingManager !== "Hiring Manager" ? def.reportingManager : "",
          workLocation:  def.workLocation || d.jobInfo?.location || "",
          workType:      def.workType || "hybrid",
        });
      } catch (e) {
        toast.error("Couldn't load offer defaults");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, resumeId, candidateName]);

  if (!isOpen) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBenefit = (i, v) => setForm((f) => { const b = [...f.benefits]; b[i] = v; return { ...f, benefits: b }; });
  const addBenefit = () => setForm((f) => ({ ...f, benefits: [...f.benefits, ""] }));
  const rmBenefit = (i) => setForm((f) => ({ ...f, benefits: f.benefits.filter((_, j) => j !== i) }));

  const submit = async () => {
    if (!form.ctcAmount || !form.positionTitle) {
      toast.error("Position and compensation are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        position: { title: form.positionTitle.trim(), level: form.positionLevel },
        department: form.department,
        salary: { amount: Number(form.ctcAmount), currency: form.ctcCurrency, frequency: form.ctcFrequency },
        startDate: form.startDate,
        benefits: form.benefits.map((b) => b.trim()).filter(Boolean),
        reportingManager: form.reportingManager || "the hiring manager",
        workLocation: form.workLocation,
        workType: form.workType,
      };
      const r = await offerLetterApi.generateOffer(resumeId, payload);
      if (r.success === false) throw new Error(r.message || "Generation failed");
      toast.success("Offer letter generated");
      onGenerated?.(r);
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to generate offer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 font-outfit">
      <div className="bg-white rounded-meraki shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-meraki-soft-blue">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-meraki bg-meraki-coral/10 flex items-center justify-center">
              <FileSignature className="w-5 h-5 text-meraki-coral" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-meraki-indigo">Generate Offer Letter</h2>
              <p className="text-xs text-meraki-graphite/50">
                For {meta.candidate || "candidate"}{meta.company?.name ? ` · ${meta.company.name}` : ""}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-meraki-graphite/40 hover:text-meraki-graphite"><X className="w-6 h-6" /></button>
        </div>

        {/* Body */}
        {loading || !form ? (
          <div className="p-12 flex items-center gap-2 text-meraki-indigo/60"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        ) : (
          <div className="p-6 overflow-y-auto space-y-5">
            {/* Position */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Position title" required>
                <input value={form.positionTitle} onChange={(e) => set("positionTitle", e.target.value)} className="inp" />
              </Field>
              <Field label="Level">
                <select value={form.positionLevel} onChange={(e) => set("positionLevel", e.target.value)} className="inp">
                  {LEVELS.map((l) => <option key={l}>{l}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Department"><input value={form.department} onChange={(e) => set("department", e.target.value)} className="inp" /></Field>

            {/* CTC */}
            <div>
              <label className="lbl">Compensation (CTC) <span className="text-meraki-coral">*</span></label>
              <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                <select
                  value={form.ctcCurrency}
                  onChange={(e) => set("ctcCurrency", e.target.value)}
                  style={{padding:'0.55rem 0.7rem', border:'1px solid #E8EEFF', borderRadius:'10px', fontSize:'0.9rem', background:'#fff', flexShrink:0, width:'90px'}}
                >
                  {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.ctcAmount}
                  onChange={(e) => set("ctcAmount", e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="e.g. 1800000"
                  style={{padding:'0.55rem 0.7rem', border:'1px solid #E8EEFF', borderRadius:'10px', fontSize:'0.9rem', background:'#fff', flex:'1 1 0', minWidth:'80px'}}
                />
                <select
                  value={form.ctcFrequency}
                  onChange={(e) => set("ctcFrequency", e.target.value)}
                  style={{padding:'0.55rem 0.7rem', border:'1px solid #E8EEFF', borderRadius:'10px', fontSize:'0.9rem', background:'#fff', flexShrink:0, width:'120px'}}
                >
                  {FREQUENCIES.map((f) => <option key={f} value={f}>per {f}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Start date"><input type="date" value={form.startDate} onChange={(e) => set("startDate", e.target.value)} className="inp" /></Field>
              <Field label="Reporting manager"><input value={form.reportingManager} onChange={(e) => set("reportingManager", e.target.value)} placeholder="e.g. Priya Sharma" className="inp" /></Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Work location"><input value={form.workLocation} onChange={(e) => set("workLocation", e.target.value)} placeholder="e.g. Bangalore" className="inp" /></Field>
              <Field label="Work type">
                <select value={form.workType} onChange={(e) => set("workType", e.target.value)} className="inp capitalize">
                  {WORK_TYPES.map((w) => <option key={w} value={w} className="capitalize">{w}</option>)}
                </select>
              </Field>
            </div>

            {/* Benefits */}
            <div>
              <label className="lbl">Benefits</label>
              <div className="space-y-2">
                {form.benefits.map((b, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={b} onChange={(e) => setBenefit(i, e.target.value)} className="inp flex-1" placeholder="Benefit" />
                    <button onClick={() => rmBenefit(i)} className="px-2 text-meraki-coral hover:bg-meraki-coral-50 rounded-meraki"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={addBenefit} className="text-sm text-meraki-indigo font-medium flex items-center gap-1 hover:underline"><Plus className="w-4 h-4" /> Add benefit</button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-meraki-soft-blue flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-meraki-graphite/60 hover:text-meraki-graphite text-sm">Cancel</button>
          <button onClick={submit} disabled={saving || loading} className="btn-meraki text-sm disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
            {saving ? "Generating…" : "Generate offer"}
          </button>
        </div>
      </div>

      <style>{`
        .inp { padding:0.55rem 0.7rem; border:1px solid #E8EEFF; border-radius:10px; font-size:0.9rem; width:100%; min-width:0; background:#fff; }
        .inp:focus { outline:none; border-color:#152B68; box-shadow:0 0 0 2px rgba(21,43,104,.12); }
        .lbl { display:block; font-size:0.8rem; font-weight:600; color:#2B2B2B; margin-bottom:0.35rem; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="lbl">{label}{required && <span className="text-meraki-coral"> *</span>}</label>
      {children}
    </div>
  );
}
